import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AthleticLiveElasticApiService, AthleticLiveFirebaseApiService } from '@athleticnet/athletic-live-api';
import { AthleteDetail } from '@athleticnet/athletic-live-api-models';
import { BehaviorSubject, distinctUntilChanged, firstValueFrom, Observable, switchMap } from 'rxjs';
import { CurrentMeetService } from './current-meet.service';

@Injectable({
  providedIn: 'root',
})
export class AthleteDetailCacheService {
  private _subscribedAthleteDetails: {
    [athleteId: number]: { old: boolean; subject: BehaviorSubject<AthleteDetail> };
  } = {};
  public readonly subscribedAthleteDetails: { [athleteId: number]: Observable<AthleteDetail> } = {};

  constructor(
    private currentMeetService: CurrentMeetService,
    private firebaseApi: AthleticLiveFirebaseApiService,
    private elasticApi: AthleticLiveElasticApiService,
  ) {
    this.currentMeetService.updateMeet
      .pipe(
        takeUntilDestroyed(),
        distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
        switchMap((meet) => {
          return this.firebaseApi.getUpdatedAthleteDetails(meet?.id ?? 0);
        }),
      )
      .subscribe((data) => {
        if (data && data.athleteIds) {
          Object.values(data.athleteIds).forEach((athleteId) => {
            if (this._subscribedAthleteDetails[athleteId]) {
              this._subscribedAthleteDetails[athleteId].old = true;
              if (this._subscribedAthleteDetails[athleteId].subject.observed) {
                // if there is an active subscription, refresh immediately
                // otherwise mark as old and follow list will call refresh on init
                this.refreshAthleteDetail(athleteId);
              }
            }
          });
        }
      });
  }

  // to be called when follows change
  public async updateAthleteList(athleteIds: number[]): Promise<void> {
    // add any new athletes followed
    for (const athleteId of athleteIds) {
      // fetch athlete detail doc if not already cached and create observable
      if (!this._subscribedAthleteDetails[athleteId]) {
        const doc = await firstValueFrom(this.elasticApi.getAthleteDetail(athleteId));
        this._subscribedAthleteDetails[athleteId] = {
          old: false,
          subject: new BehaviorSubject<AthleteDetail>(doc),
        };
        this.subscribedAthleteDetails[athleteId] = this._subscribedAthleteDetails[athleteId].subject.asObservable();
      }
    }

    // remove any athlete details no longer followed
    for (const athleteId in this._subscribedAthleteDetails) {
      if (!athleteIds.includes(+athleteId)) {
        delete this._subscribedAthleteDetails[+athleteId];
        delete this.subscribedAthleteDetails[+athleteId];
      }
    }
  }

  public async refreshAthleteDetails(athleteIds: number[]): Promise<void> {
    for (const athleteId of athleteIds) {
      if (this._subscribedAthleteDetails[athleteId]?.old) {
        const doc = await firstValueFrom(this.elasticApi.getAthleteDetail(athleteId));
        this._subscribedAthleteDetails[athleteId].subject.next(doc);
        this._subscribedAthleteDetails[athleteId].old = false;
      }
    }
  }

  private async refreshAthleteDetail(athleteId: number): Promise<void> {
    if (this._subscribedAthleteDetails[athleteId]) {
      const doc = await firstValueFrom(this.elasticApi.getAthleteDetail(athleteId));
      this._subscribedAthleteDetails[athleteId].subject.next(doc);
      this._subscribedAthleteDetails[athleteId].old = false;
    }
  }
}
